import ctl from '@netlify/classnames-template-literals'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useGeocodeMultiple } from '@/hooks'
import { SpinLoader } from '@/ui'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'

const OSM_TILE_URL_DEFAULT = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const osmTileUrl = import.meta.env.VITE_OSM_TILE_URL || OSM_TILE_URL_DEFAULT

// Jobilla-style marker icon using brand gradient (purple to blue)
const jobillaMarkerSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44" width="32" height="44">
  <defs>
    <linearGradient id="jobilla-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#710DE0"/>
      <stop offset="100%" stop-color="#304FFB"/>
    </linearGradient>
  </defs>
  <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 28 16 28s16-16 16-28c0-8.8-7.2-16-16-16z" fill="url(#jobilla-gradient)"/>
  <circle cx="16" cy="16" r="7" fill="white"/>
  <circle cx="16" cy="16" r="4" fill="#07C4FC"/>
</svg>
`

const jobillaIcon = new L.Icon({
	iconUrl: `data:image/svg+xml;base64,${btoa(jobillaMarkerSvg)}`,
	iconSize: [32, 44],
	iconAnchor: [16, 44],
	popupAnchor: [0, -44],
})

export type CityLocationMapProps = {
	cities: Array<{
		id: string
		name: string
		radius: number
		country: { name: string } | null
	}>
	height?: number
}

type GeocodedCity = {
	id: string
	name: string
	lat: number
	lng: number
	radius: number
}

export function CityLocationMap({ cities, height = 300 }: CityLocationMapProps) {
	const mapContainerRef = useRef<HTMLDivElement>(null)
	const mapRef = useRef<L.Map | null>(null)
	const layersRef = useRef<L.Layer[]>([])

	const citiesToGeocode = useMemo(
		() =>
			cities.map((city) => ({
				id: city.id,
				name: city.name,
				countryName: city.country?.name ?? '',
				radius: city.radius,
			})),
		[cities],
	)

	const { cities: geocodedCities, isLoading, pendingCount, errorCount } = useGeocodeMultiple({
		cities: citiesToGeocode,
	})

	const geocodedKey = geocodedCities.map((c) => `${c.id}:${c.lat}:${c.lng}`).join('|')

	// useLayoutEffect ensures map.remove() runs before React removes the DOM container,
	// preventing Leaflet from accessing detached DOM nodes (_leaflet_pos error).
	useLayoutEffect(() => {
		if (!mapContainerRef.current) { return }

		const map = L.map(mapContainerRef.current, {
			center: [54.526, 15.2551],
			zoom: 4,
			scrollWheelZoom: false,
		})

		L.tileLayer(osmTileUrl, {
			attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		}).addTo(map)

		mapRef.current = map

		return () => {
			map.remove()
			mapRef.current = null
			layersRef.current = []
		}
	}, [])

	useEffect(() => {
		const map = mapRef.current
		if (!map) { return }

		for (const layer of layersRef.current) {
			map.removeLayer(layer)
		}
		layersRef.current = []

		if (geocodedCities.length === 0) { return }

		const newLayers: L.Layer[] = []
		for (const city of geocodedCities) {
			const marker = L.marker([city.lat, city.lng], { icon: jobillaIcon }).addTo(map)
			const circle = L.circle([city.lat, city.lng], {
				radius: city.radius * 1000,
				color: '#710DE0',
				fillColor: '#304FFB',
				fillOpacity: 0.15,
			}).addTo(map)
			newLayers.push(marker, circle)
		}
		layersRef.current = newLayers

		if (geocodedCities.length === 1) {
			map.setView([geocodedCities[0].lat, geocodedCities[0].lng], 8)
		} else {
			const bounds = L.latLngBounds(geocodedCities.map((c) => [c.lat, c.lng]))
			map.fitBounds(bounds, { padding: [50, 50] })
		}
	}, [geocodedKey])

	const containerClass = ctl(`
		relative
		w-full
		rounded-lg
		overflow-hidden
		border
		border-grey-200
		bg-grey-50
	`)

	if (cities.length === 0) {
		return (
			<div className={containerClass} style={{ height }}>
				<div className="absolute inset-0 flex items-center justify-center text-grey-500">
					Select cities to see them on the map
				</div>
			</div>
		)
	}

	return (
		<div className={containerClass} style={{ height }}>
			{isLoading && (
				<div className="absolute top-2 right-2 z-[1000] bg-white/90 rounded px-2 py-1 text-sm text-grey-600 flex items-center gap-2">
					<SpinLoader />
					Loading {pendingCount} location{pendingCount !== 1 ? 's' : ''}...
				</div>
			)}
			{errorCount > 0 && (
				<div className="absolute top-2 left-2 z-[1000] bg-red-50 border border-red-200 rounded px-2 py-1 text-sm text-red-600">
					{errorCount} location{errorCount !== 1 ? 's' : ''} could not be found
				</div>
			)}
			<div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
		</div>
	)
}
